import { api } from '../api/client.js';
import { audioEvents } from '../audio/audioEvents.js';
import { applyOp } from '../farm/farmRules.mjs';
import { serverNow } from './serverClock.mjs';

class GameState {
  constructor() {
    this.raw = null;
    this.listeners = new Set();
    this._seq = 0;      // stamp handed out to each state-returning request
    this._applied = 0;  // seq of the newest response applied to raw
  }

  on(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  emit() {
    // Dev-only time-of-day override (set via the T/R hotkeys in main.js): force the phase
    // on every emit so server refreshes don't undo it. No effect unless explicitly set.
    if (this._devPhase && this.raw?.time) this.raw.time.phase = this._devPhase;
    for (const cb of this.listeners) {
      try { cb(this.raw); } catch (e) { console.error('gameState listener error:', e); }
    }
  }

  // Run a state-returning API call with an ordering guard: a response is applied to
  // `raw` only if no later-sent request has already been applied. Without this, any
  // slow in-flight response (the 30s refresh poll, the scene-change grease flush)
  // landing after a newer action would clobber raw with a stale snapshot — e.g. a
  // freshly parked bike teleporting back to its previous spot. The dropped response
  // is still returned to the caller (it IS that caller's own action result); the
  // action sfx also plays regardless, since the action itself succeeded.
  async _apply(call, sfx = null) {
    const seq = ++this._seq;
    const state = await call();
    if (seq > this._applied) {
      this._applied = seq;
      this.raw = state;
      this.emit();
    }
    if (sfx) audioEvents.emit(sfx);
    return state;
  }

  // Clear all state (logout) AND fence off every in-flight response so a slow
  // request from the old session can't resurrect its state on the login screen.
  invalidate() {
    this._applied = ++this._seq;
    this.raw = null;
  }

  async refresh()           { return this._apply(() => api.getState()); }

  // Farm (spec 2026-09-22 §3). Ops are applied locally first (applyFarmLocal, no emit — the
  // scene emits once per batch of local changes), then flushed in one request whose full
  // snapshot replaces raw through the ordering guard.
  async farmActions(actions)     { return this._apply(() => api.farmActions(actions)); }
  applyFarmLocal(op, nowMs = serverNow()) {
    if (!this.raw?.farm || !this.raw?.catalog?.farm) return { ok: false, reason: 'NO_STATE' };
    return applyOp(this.raw, op, nowMs);
  }

  async unlockHouse()       { return this._apply(() => api.unlockHouse()); }
  async buyFurniture(item)  { return this._apply(() => api.buyFurniture(item), 'buy'); }
  async buyDecoration(item) { return this._apply(() => api.buyDecoration(item), 'buy'); }
  async unlockRoom(room)    { return this._apply(() => api.unlockRoom(room)); }
  async resetGame()         { return this._apply(() => api.resetGame()); }
  async buyBuda(item)           { return this._apply(() => api.buyBuda(item), 'buy'); }
  async buyFood(item)           { return this._apply(() => api.buyFood(item), 'buy'); }
  async buyAnimal(type, budaId = null) { return this._apply(() => api.buyAnimal(type, budaId), 'buy'); }
  async buyClothing(item)       { return this._apply(() => api.buyClothing(item), 'buy'); }
  async buyBoardGame(gameId)    { return this._apply(() => api.buyBoardGame(gameId), 'buy'); }
  async placeObject(type, item, scene, x, y) { return this._apply(() => api.placeObject(type, item, scene, x, y), 'place'); }
  async moveObject(pid, x, y)   { return this._apply(() => api.moveObject(pid, x, y), 'place'); }
  async removeObject(pid)       { return this._apply(() => api.removeObject(pid)); }
  async sellObject(pid)         { return this._apply(() => api.sellObject(pid), 'sell'); }
  async toggleFlip(pid)         { return this._apply(() => api.toggleFlip(pid)); }
  async buyBike(design)                   { return this._apply(() => api.buyBike(design), 'buy'); }
  async sellBike(bike)                    { return this._apply(() => api.sellBike(bike), 'sell'); }
  async buyBikeGrease()                   { return this._apply(() => api.buyBikeGrease(), 'buy'); }
  async greaseBike(bike)                  { return this._apply(() => api.greaseBike(bike)); }
  async consumeBikeGrease(amount, bike)   { return this._apply(() => api.consumeBikeGrease(amount, bike)); }
  async parkBike(scene, x, bike, facing, y) { return this._apply(() => api.parkBike(scene, x, bike, facing, y)); }
  // Fire-and-forget: persists current scene/position. Deliberately does NOT emit —
  // it's called frequently while walking and no live UI depends on it (BootScene reads
  // it fresh on load). Keep raw.location roughly in sync for completeness.
  async setLocation(scene, x, y, facing = null) {
    try {
      await api.setLocation(scene, x, y, facing);
      if (this.raw) this.raw.location = { scene, x, y, facing };
    } catch (_) { /* best-effort */ }
  }
  async feedAnimal(aid)         { return this._apply(() => api.feedAnimal(aid)); }
  async petAnimal(aid)          { return this._apply(() => api.petAnimal(aid)); }
  async collectProduct(aid)     { return this._apply(() => api.collectProduct(aid)); }
  async equipClothing(item)     { return this._apply(() => api.equipClothing(item)); }
  async unequipClothing(cat)    { return this._apply(() => api.unequipClothing(cat)); }
  async sellProduct(item, qty=1){ return this._apply(() => api.sellProduct(item, qty), 'sell'); }
  async setClothTweak(item, tw) { return this._apply(() => api.setClothTweak(item, tw)); }
  async resetClothTweak(item)   { return this._apply(() => api.resetClothTweak(item)); }
  async upgradeHouse()           { return this._apply(() => api.upgradeHouse(), 'upgrade'); }
  async unlockBackyard()         { return this._apply(() => api.unlockBackyard()); }
  async upgradeBackyard()        { return this._apply(() => api.upgradeBackyard(), 'upgrade'); }
  async devAddMoney()            { return this._apply(() => api.devAddMoney(), 'sell'); }

  // Driving school — responses carry extra `quiz` / `quiz_result` keys next to the state;
  // _apply stores the whole object as raw (extra keys are harmless) and returns it.
  async drivingBuyCourse()          { return this._apply(() => api.drivingBuyCourse(), 'buy'); }
  async drivingQuizStart(kind)      { return this._apply(() => api.drivingQuizStart(kind)); }
  async drivingQuizAnswer(id, idx)  { return this._apply(() => api.drivingQuizAnswer(id, idx)); }
  async drivingQuizAbort()          { return this._apply(() => api.drivingQuizAbort()); }
  async drivingDevGrantLicence()    { return this._apply(() => api.drivingDevGrantLicence()); }
  // NOT via _apply: the lessons response is study material, not a state snapshot.
  async drivingLessons()            { return api.drivingLessons(); }

  // Practical lessons + exam (spec 3) — responses carry an extra `practical` key next
  // to the state (the session dict for start, or the lesson/finish result); _apply
  // stores the whole object as raw (harmless) and returns it.
  async drivingLessonStart(n)                    { return this._apply(() => api.drivingLessonStart(n)); }
  async drivingLessonFinish(tasks)               { return this._apply(() => api.drivingLessonFinish(tasks)); }
  async drivingLessonAbort()                     { return this._apply(() => api.drivingLessonAbort()); }
  async drivingPracticalStart()                  { return this._apply(() => api.drivingPracticalStart()); }
  async drivingPracticalFinish(tasks)            { return this._apply(() => api.drivingPracticalFinish(tasks)); }
  async drivingPracticalAbort()                  { return this._apply(() => api.drivingPracticalAbort()); }

  // Cars — every response is a full state snapshot, so they all go through _apply.
  // `carsLeaveGarage` / `carsRefuel` responses carry an extra `car` / `refuel` key next
  // to the state; _apply stores the whole object as raw (harmless) and returns it, so the
  // caller can read `out.car` / `out.refuel`.
  async carsBuy(model)                    { return this._apply(() => api.carsBuy(model), 'buy'); }
  async carsSell(carId)                   { return this._apply(() => api.carsSell(carId), 'sell'); }
  async carsRepair(carId)                 { return this._apply(() => api.carsRepair(carId), 'upgrade'); }
  async carsLeaveGarage(carId)            { return this._apply(() => api.carsLeaveGarage(carId)); }
  async carsPark(carId, x, facing)        { return this._apply(() => api.carsPark(carId, x, facing)); }
  async carsParkGarage(carId)             { return this._apply(() => api.carsParkGarage(carId)); }
  async carsSetChargeLimit(carId, limit)  { return this._apply(() => api.carsSetChargeLimit(carId, limit)); }
  async carsRefuel(carId, nozzle, liters) { return this._apply(() => api.carsRefuel(carId, nozzle, liters)); }
  async carsBreakDown(carId)              { return this._apply(() => api.carsBreakDown(carId)); }
  async carsRescue(carId)                 { return this._apply(() => api.carsRescue(carId)); }
  async carsDevAddCar(model)              { return this._apply(() => api.carsDevAddCar(model)); }

  // Fire-and-forget odometer/fuel tick, sent every few metres while driving. Deliberately
  // NOT via _apply: it would emit (re-rendering the world) many times per drive, and its
  // snapshot is only ever a hair fresher than what the drive already knows locally — the
  // park/refuel action at the end of the drive brings raw back in sync. Returns the server's
  // error code (e.g. CAR_BAD_REPORT) so the controller can stop the car, or null on success.
  async carsDriveReport(carId, meters, fuel) {
    try {
      await api.carsDriveReport(carId, meters, fuel);
      return null;
    } catch (err) {
      return err?.detail?.error_code ?? err?.error_code ?? 'UNKNOWN';
    }
  }
}

export const gameState = new GameState();
